import { message } from 'antd';
import { AxiosError } from 'axios';
import { MyEventTarget } from '@/scripts/extends';
import { userApis } from '@/configs/apis/user';
import Paths from '@/configs/router/paths';
import { pushLog } from '../utils/devUtils';
import requests_, { instances } from './requests';
import type { MyRequests, MyAxiosResponseData, MyAxiosResponse } from './types';
import { encryptedData, decryptedData } from './crypto';

export const requests = Object.assign(requests_ as MyRequests, {
  event: new MyEventTarget<{ error: AxiosError<MyAxiosResponseData, any> }>(),
});

for (const k in instances) {
  const instance = instances[k as keyof typeof instances];
  const {
    interceptors: { request, response },
  } = instance;

  // Request handler
  request.use(
    (config) => {
      const token = userApis.getToken();
      const isEncrypt = import.meta.env.VITE_HTTP_ENCRYPT === 1;
      config.headers = Object.assign(
        {
          Authorization: 'Bearer ' + token,
          'X-Client-Version': import.meta.env.VITE_HTTP_ENCRYPT_VERSION,
          'X-Encrypt': false,
        },
        config.headers
      );

      if (
        config.data &&
        config.headers['Content-Type'] !== 'multipart/form-data' &&
        !config.headers['Content-Type']?.toString()?.startsWith('image/') &&
        isEncrypt
      ) {
        config.headers['X-Encrypt'] = true;
        config.data = encryptedData(JSON.stringify(config.data));
        config.transformResponse = [
          (data) => {
            const responseData = JSON.parse(data);
            if (responseData?.data) {
              responseData.data = JSON.parse(decryptedData(responseData.data));
            }
            return responseData;
          },
        ];
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response handler
  response.use((response: MyAxiosResponse) => {
    const {
      data,
      config,
      request,
      data: { code, message: msg },
      config: { msg: msgArg, disableOnyResponseData },
    } = response;
    pushLog('response', response.data);
    if (data && code !== 1) {
      throw new AxiosError<MyAxiosResponseData>(
        msg,
        String(code),
        config,
        request,
        response
      );
    }

    // Success message
    if (msgArg) {
      message.success({
        content: typeof msgArg === 'string' ? msgArg : msg || 'Success',
      });
    }
    // Only response data

    return disableOnyResponseData ? response : (data as any);
  });

  response.use(null, (error: AxiosError<MyAxiosResponseData>) => {
    const response = error.response as MyAxiosResponse;
    if (response) {
      const {
        status,
        data,
        config: { disableErrorEvent, disableErrorMsg },
      } = response;
      // Error event
      if (!disableErrorEvent) {
        requests.event.dispatchEvent('error', error);
      }
      // Error msg
      if (!disableErrorMsg) {
        switch (status) {
          case 401:
            message.destroy();
            message.error({
              content: 'Unauthorized',
            });
            userApis.removeUserStorage();
            window.location.href = Paths.LOGIN;
            break;
          case 403:
            message.error({
              content: 'Not Permission',
            });
            break;
          default:
            message.error({
              content: data?.message
                ? data.message
                : error.message || 'Unknown Network Error.',
            });

            break;
        }
      }
    }
    return Promise.reject(error);
  });
}

export default requests;
